// src/pages/StudyPartnerPage.jsx (WP-6 Slice 2)
//
// Student AI Study Partner page. Pick an available/owned pack, then chat with
// the reading-companion AI (AiStudyPartnerPanel -> onAiChat CF). Entitlement
// (active = status+expiry, read-only listener) decides full vs free-preview.
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconSparkles, IconBookOpen } from '../components/icons.jsx';
import AiStudyPartnerPanel from '../components/AiStudyPartnerPanel.jsx';
import { useEntitlements } from '../storage/useEntitlements.js';

export const StudyPartnerPage = ({ state }) => {
    const { packId: routePackId } = useParams();
    const navigate = useNavigate();
    const { activePackIds } = useEntitlements();

    const packs = (state.packs || []).filter(
        (p) => p.status === 'available' || activePackIds.has(p.id)
    );
    const [selectedId, setSelectedId] = useState(routePackId || packs[0]?.id || null);
    const selected = packs.find((p) => p.id === selectedId) || null;
    const entitled = selected ? activePackIds.has(selected.id) : false;

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture page-root-text-study">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12">
                <div className="mb-8 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-book-accent/10 border border-book-accent flex items-center justify-center">
                        <IconSparkles className="w-5 h-5 text-book-accent" />
                    </div>
                    <div>
                        <div className="font-display text-book-accent text-xs font-semibold">AI Study Partner</div>
                        <h1 className="font-display text-2xl md:text-3xl text-book-text leading-tight">Study with your reading companion</h1>
                    </div>
                </div>

                {packs.length === 0 ? (
                    <div className="border border-book-accent/20 bg-book-page/40 p-10 text-center">
                        <p className="font-serif italic text-book-muted text-lg mb-4">
                            No study packs available yet. Browse the library to get started.
                        </p>
                        <button onClick={() => navigate('/dashboard')}
                            className="px-5 py-2.5 bg-book-accent text-black font-display text-xs hover:bg-book-accentLight transition-colors font-semibold">
                            Go to Library
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[18rem_1fr] gap-6">
                        {/* Pack picker */}
                        <div className="space-y-2">
                            <div className="font-display text-book-muted text-xs mb-2 font-semibold">Choose a pack</div>
                            {packs.map((p) => {
                                const owned = activePackIds.has(p.id);
                                const active = p.id === selectedId;
                                return (
                                    <button key={p.id}
                                        onClick={() => { setSelectedId(p.id); navigate('/study-partner/' + p.id, { replace: true }); }}
                                        className={`w-full text-left p-3 border transition-colors flex items-center gap-3 ${
                                            active ? 'border-book-accent bg-book-accent/10' : 'border-book-accent/20 bg-book-page/40 hover:border-book-accent/50'
                                        }`}>
                                        <IconBookOpen className="w-4 h-4 text-book-accent flex-shrink-0" />
                                        <span className="flex-grow">
                                            <span className="block font-display text-book-text text-sm leading-tight">{p.label}</span>
                                            <span className="block font-serif italic text-book-muted text-xs font-semibold">
                                                {owned ? 'Full access' : 'Free preview'}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Chat panel */}
                        <div className="h-[70vh]">
                            {selected && (
                                <AiStudyPartnerPanel
                                    key={selected.id}
                                    packId={selected.id}
                                    packTitle={selected.label}
                                    entitled={entitled} />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StudyPartnerPage;
